/**
 * Position Resolver
 * Resolves polymorphic layer positions to absolute coordinates
 */

import type {
  LayerPosition,
  Alignment,
  HorizontalConstraint,
  VerticalConstraint,
} from '../../types/position';
import type { FormatCategory, Layer, ResponsiveOverrides } from '../../types/layers';
import type { AdFormat } from '../../types/formats';

// Resolved position with absolute coordinates
export interface ResolvedPosition {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

// Container dimensions for resolution
export interface ContainerDimensions {
  width: number;
  height: number;
}

// Layer dimensions for alignment calculations
export interface LayerDimensions {
  width: number;
  height: number;
}

/**
 * Resolve alignment position to absolute coordinates
 */
function resolveAlignmentPosition(
  alignment: Alignment,
  offsetX: number,
  offsetY: number,
  container: ContainerDimensions,
  layer: LayerDimensions
): ResolvedPosition {
  let x = 0;
  let y = 0;

  // Horizontal alignment
  if (alignment.includes('Left')) {
    x = offsetX;
  } else if (alignment.includes('Right')) {
    x = container.width - layer.width + offsetX;
  } else {
    // Center
    x = (container.width - layer.width) / 2 + offsetX;
  }

  // Vertical alignment
  if (alignment.startsWith('Top')) {
    y = offsetY;
  } else if (alignment.startsWith('Bottom')) {
    y = container.height - layer.height + offsetY;
  } else {
    // Center
    y = (container.height - layer.height) / 2 + offsetY;
  }

  return { x, y };
}

/**
 * Resolve horizontal constraint to x position and optional width
 */
function resolveHorizontalConstraint(
  constraint: HorizontalConstraint,
  container: ContainerDimensions,
  layerWidth: number,
  baseWidth?: number // For scale mode
): { x: number; width?: number } {
  switch (constraint.type) {
    case 'left':
      return { x: constraint.offset };
    case 'right':
      return { x: container.width - layerWidth - constraint.offset };
    case 'center':
      return { x: (container.width - layerWidth) / 2 + (constraint.offset ?? 0) };
    case 'stretch':
      return {
        x: constraint.left,
        width: container.width - constraint.left - constraint.right,
      };
    case 'scale': {
      // Scale position and size proportionally
      const scale = baseWidth ? container.width / baseWidth : 1;
      return { x: 0, width: layerWidth * scale };
    }
  }
}

/**
 * Resolve vertical constraint to y position and optional height
 */
function resolveVerticalConstraint(
  constraint: VerticalConstraint,
  container: ContainerDimensions,
  layerHeight: number,
  baseHeight?: number // For scale mode
): { y: number; height?: number } {
  switch (constraint.type) {
    case 'top':
      return { y: constraint.offset };
    case 'bottom':
      return { y: container.height - layerHeight - constraint.offset };
    case 'center':
      return { y: (container.height - layerHeight) / 2 + (constraint.offset ?? 0) };
    case 'stretch':
      return {
        y: constraint.top,
        height: container.height - constraint.top - constraint.bottom,
      };
    case 'scale': {
      // Scale position and size proportionally
      const scale = baseHeight ? container.height / baseHeight : 1;
      return { y: 0, height: layerHeight * scale };
    }
  }
}

/**
 * Main position resolver function
 * Resolves any LayerPosition to absolute coordinates
 */
export function resolvePosition(
  position: LayerPosition,
  container: ContainerDimensions,
  layer: LayerDimensions,
  baseContainer?: ContainerDimensions // For scale mode
): ResolvedPosition {
  switch (position.mode) {
    case 'absolute':
      return { x: position.x, y: position.y };

    case 'alignment':
      return resolveAlignmentPosition(
        position.alignment,
        position.offsetX ?? 0,
        position.offsetY ?? 0,
        container,
        layer
      );

    case 'constraints': {
      const horizontal = resolveHorizontalConstraint(
        position.horizontal,
        container,
        layer.width,
        baseContainer?.width
      );
      const vertical = resolveVerticalConstraint(
        position.vertical,
        container,
        layer.height,
        baseContainer?.height
      );

      return {
        x: horizontal.x,
        y: vertical.y,
        width: horizontal.width,
        height: vertical.height,
      };
    }
  }
}

/**
 * Determine the format category for responsive overrides
 */
export function getFormatCategory(format: AdFormat): FormatCategory {
  const { width, height } = format;
  const aspectRatio = width / height;
  const area = width * height;

  // Tiny: very small ads (e.g., 120x60 banners)
  if (height <= 120 || area < 50000) {
    return 'tiny';
  }

  // Small: compact ads (e.g., 300x250 MPU)
  if (area < 150000) {
    return 'small';
  }

  // Wide: landscape orientation
  if (aspectRatio > 1.5) {
    return 'wide';
  }

  // Tall: portrait orientation
  if (aspectRatio < 0.75) {
    return 'tall';
  }

  // Square: near 1:1 ratio
  if (aspectRatio >= 0.9 && aspectRatio <= 1.1) {
    return 'square';
  }

  // Medium: everything else
  return 'medium';
}

/**
 * Get effective layer properties with responsive overrides applied
 */
export function getEffectiveLayerProps<T extends Layer>(
  layer: T,
  format: AdFormat
): T {
  if (!layer.responsiveOverrides) {
    return layer;
  }

  const category = getFormatCategory(format);
  const override = layer.responsiveOverrides[category];

  if (!override) {
    return layer;
  }

  // Merge override into layer (shallow merge)
  return {
    ...layer,
    ...override,
    // Preserve layer identity
    id: layer.id,
    type: layer.type,
    name: layer.name,
  };
}

/**
 * Calculate layer bounds including transforms
 */
export function calculateLayerBounds(
  position: ResolvedPosition,
  layer: LayerDimensions,
  transform?: { scaleX?: number; scaleY?: number; rotation?: number }
): { x: number; y: number; width: number; height: number } {
  const scaleX = transform?.scaleX ?? 1;
  const scaleY = transform?.scaleY ?? 1;
  const width = (position.width ?? layer.width) * scaleX;
  const height = (position.height ?? layer.height) * scaleY;

  // For rotated layers, we'd need to calculate the bounding box
  // For now, just return the scaled dimensions
  return {
    x: position.x,
    y: position.y,
    width,
    height,
  };
}

/**
 * Check if a point is within a layer's bounds
 */
export function isPointInLayer(
  point: { x: number; y: number },
  position: ResolvedPosition,
  layer: LayerDimensions
): boolean {
  const bounds = calculateLayerBounds(position, layer);
  return (
    point.x >= bounds.x &&
    point.x <= bounds.x + bounds.width &&
    point.y >= bounds.y &&
    point.y <= bounds.y + bounds.height
  );
}

/**
 * Find the topmost layer at a given point
 */
export function findLayerAtPoint(
  point: { x: number; y: number },
  layers: Layer[],
  container: ContainerDimensions,
  getLayerDimensions: (layer: Layer) => LayerDimensions
): Layer | null {
  // Iterate in reverse (top to bottom in z-order)
  for (let i = layers.length - 1; i >= 0; i--) {
    const layer = layers[i];
    if (!layer.visible) continue;

    const dims = getLayerDimensions(layer);
    const resolved = resolvePosition(layer.position, container, dims);

    if (isPointInLayer(point, resolved, dims)) {
      return layer;
    }
  }

  return null;
}
